import '../../../jobs/components/list/JobsList.scss';
import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement, useMemo } from 'react';
import { GTable, IGColumnProps } from '../../../../__legacy__/components/table/GTable';
import { formatSecondsToTime, toLocaleDateTimeString } from '../../../../__legacy__/helpers/date.helpers';
import { Home } from '../../../../__legacy__/routes/config/Home';
import { S_Alert } from '../../../../design/alert/S_Alert';
import { S_Link } from '../../../../design/links/S_Link';
import { ColumnLayout } from '../../../common/layouts/columnLayout/ColumnLayout';
import { JobStatusBadge } from '../../../common/statusesBadges/modelStatus/JobStatusBadge';
import { ISubTrainingJob, JOB_STATUS } from '../../../jobs/jobs.model';

interface IJobSubTrainingsProps {
  trainingJobs: ISubTrainingJob[];
  workspaceId: string;
  isLoading: boolean;
}

export const JobSubTrainings = observer((props: IJobSubTrainingsProps): ReactElement => {
  const { t } = useTranslator();
  const { trainingJobs } = props;

  const renderName = (modelItem: ISubTrainingJob): ReactElement => {
    const { workspaceId } = props;
    const { status, name, id } = modelItem;
    const METRICS_STATUSES = [JOB_STATUS.STARTING, JOB_STATUS.RUNNING, JOB_STATUS.EVALUATING, JOB_STATUS.FINISHED, JOB_STATUS.IMPORTED, JOB_STATUS.IMPORTING];
    const link = METRICS_STATUSES.includes(status)
      ? Home.Trainings.Details.Metrics.withParams({ workspaceId, jobId: id })
      : Home.Trainings.Details.Settings.withParams({ workspaceId, jobId: id });

    return (
      <S_Link
        color='text'
        to={link}
        className='link-flex'
      >
        {modelItem.isBest && (
          <FontAwesomeIcon
            className='link-icon'
            icon={faTrophy}
          />
        )}
        {name}
      </S_Link>
    );
  };

  const renderCreationDate = (modelItem: ISubTrainingJob): ReactElement => <>{toLocaleDateTimeString(modelItem.dateCreated)}</>;

  const renderProgress = (modelItem: ISubTrainingJob): ReactElement => <>{modelItem.progress === '0' ? t('na') : modelItem.progress}</>;

  const renderTrainingTime = (modelItem: ISubTrainingJob): ReactElement => {
    if ([JOB_STATUS.STARTING, JOB_STATUS.IMPORTED, JOB_STATUS.IMPORTING, JOB_STATUS.STOPPING].includes(modelItem.status) || modelItem.trainingTime === 0) {
      return <>{t('na')}</>;
    }
    return <>{formatSecondsToTime(modelItem.trainingTime)}</>;
  };

  const renderInferenceTime = (modelItem: ISubTrainingJob): ReactElement => {
    if ([JOB_STATUS.FINISHED, JOB_STATUS.IMPORTED].includes(modelItem.status) && modelItem.inferenceTime !== 0) {
      return (
        <>
          {Math.floor(modelItem.inferenceTime * 1000)
            .toString()
            .padStart(3, '0')}
        </>
      );
    }

    return <>{t('na')}</>;
  };

  const renderScore = (modelItem: ISubTrainingJob): ReactElement => <>{modelItem.score === null ? t('na') : modelItem.score.toFixed(3)}</>;

  const getModelStatus = (modelItem: ISubTrainingJob): ReactElement => {
    const { status } = modelItem;

    return (
      <div className='status-with-progress'>
        <JobStatusBadge jobStatus={status} />
      </div>
    );
  };

  const renderStatus = (modelItem: ISubTrainingJob): ReactElement => getModelStatus(modelItem);

  const renderParam =
    (name: string) =>
    (modelItem: ISubTrainingJob): ReactElement => {
      return <>{modelItem.params.find((param) => param.name === name)?.value}</>;
    };

  const columns = useMemo((): IGColumnProps<ISubTrainingJob>[] => {
    return [
      {
        headerName: t('name'),
        renderer: renderName,
      },
      {
        headerName: t('date_created'),
        renderer: renderCreationDate,
      },
      ...(trainingJobs.length > 0
        ? trainingJobs[0].params.map((param) => ({
            headerName: param.name,
            renderer: renderParam(param.name),
          }))
        : []),
      {
        headerName: t('training_time'),
        renderer: renderTrainingTime,
      },
      {
        headerName: t('inference_time'),
        renderer: renderInferenceTime,
      },

      {
        headerName: t('progress'),
        renderer: renderProgress,
      },
      {
        headerName: t('score'),
        renderer: renderScore,
      },
      {
        headerName: t('status'),
        renderer: renderStatus,
        width: 'min-content',
      },
    ];
  }, [trainingJobs]);

  return (
    <div className='tab-content'>
      <ColumnLayout size='md'>
        <div>
          <h4>{t('auto_training_jobs_list')}</h4>
          <S_Alert color='info'>
            <strong>{t('info')} </strong>
            {t('training.sub_training_banner')}
          </S_Alert>
        </div>
        <div>
          <div>
            <GTable
              isLoading={props.isLoading}
              columns={columns}
              items={trainingJobs}
              noItemsPlaceholder={t('no_training_in_auto_training')}
            />
          </div>
        </div>
      </ColumnLayout>
    </div>
  );
});
